import { AfterContentChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { BulkSubscription, Compass, CompassAccessControl, CompassCopyControl, CompassPublicationType, CompassStateModel, CompassStatus, Provider } from 'huddyapi';
import { Observable, Subscription } from 'rxjs';
import { AppComponent } from '../app.component';
import { CompassLibraryManageDialog } from '../components/compass-library-manage-dialog/compass-library-manage-dialog.component';
import { DesktopErrorHandler } from '../services/errorHandler.service';

@Component({
  selector: 'app-library',
  templateUrl: './compasses.component.html',
  styleUrls: ['./compasses.component.scss']
})
export class CompassesComponent implements OnInit, AfterContentChecked {
  @Select(state => state.compassState) compassState$: Observable<CompassStateModel>;

  @ViewChild('compassCardsContainer') compassCardsContainer: ElementRef;

  @ViewChild('search', { static: false }) set searchInput(searchInput: ElementRef) {
    this._searchInput = this.searchInput;
    if(this.shouldAutofocus && searchInput != null) {
      this.shouldAutofocus = false;
      searchInput.nativeElement.focus();
    }
  }

  providerSubscription: Subscription;
  providers: Provider[];
  @Select(state => state.compassState.providers) providers$: Observable<Provider[]>;

  get searchInput(): ElementRef {
    return this._searchInput;
  }

  set searchInputValue(searchInputValue: string) {
    this._searchInputValue = searchInputValue;
    this.filterString = this.normalizeName(searchInputValue || '');
  }

  get searchInputValue(): string {
    return this._searchInputValue;
  }
  
  _searchInput: ElementRef;
  _searchInputValue: string;
  shouldAutofocus: boolean;
  filterString: string;
  compassColumnStyles: string;
  loadingArray: void[];
  privateCompasses: Compass[];
  subscriptions: BulkSubscription[];
  manageableCompasses: Compass[];
  copies: Map<number, Compass[]>;
  copying: boolean;

  MIN_CARD_WIDTH: number = 270;
  APPROX_MAX_CARD_WIDTH: number = 400;
  CARD_GAP: number = 18;

  constructor(
    private dialog: MatDialog,
    private errorHandler: DesktopErrorHandler,
    public router: Router
  ) {
    this.shouldAutofocus = true;
    this.loadingArray = [null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null];
    this.searchInputValue = '';
    this.compassColumnStyles = '';
    this.privateCompasses = [];
    this.subscriptions = [];
    this.manageableCompasses = [];
    this.copies = new Map<number, Compass[]>();
    this.copying = false;
  }

  ngOnInit() {
    this.copying = false;
    this.compassState$.subscribe((model: CompassStateModel) => {
      this.subscriptions = model.subscriptions;
      this.privateCompasses = model.compasses;
      this.handleUpdate();
    });
    AppComponent.loadedSubject.subscribe(() => this.handleUpdate());
    this.providerSubscription = this.providers$.subscribe((providers: Provider[]) => {
      this.providers = providers;
    });
  }

  ngOnDestroy() {
    this.providerSubscription.unsubscribe();
  }

  ngAfterContentChecked() {
    this.compassColumnStyles = this.getCompassColumnStyles();
  }

  handleUpdate() {
    if(this.isLoading())
      return;
    const compassCompare: (a: Compass, b: Compass) => number = (a: Compass, b: Compass): number => {
      let comp: number = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      if(comp != 0) return comp;
      return a.id - b.id;
    }
    const subscriptionIds: Map<number, void> = new Map<number, void>(
      this.subscriptions.map((subscription: BulkSubscription) => [subscription.compassSourceId, null])
    );
    let manageableCompasses: Compass[] = this.subscriptions.map((subscription: BulkSubscription): Compass => subscription.compass);
    let copies: Map<number, Compass[]> = new Map<number, Compass[]>();
    let sourceMap: Map<number, number> = new Map<number, number>(
      this.privateCompasses.map((compass: Compass) => [compass.id, compass.sourceId])
    );
    this.privateCompasses.forEach((compass: Compass) => {
      if(compass.status != CompassStatus.PUBLISHED || subscriptionIds.has(compass.id))
        return;
      let sourceId: number = compass.sourceId;
      while(sourceId && !subscriptionIds.has(sourceId))
        sourceId = sourceMap.get(sourceId);
      if(sourceId) {
        if(copies.has(sourceId))
          copies.get(sourceId).push(compass);
        else
          copies.set(sourceId, [compass]);
      }
    });
    manageableCompasses.sort(compassCompare);
    Array.from(copies.values()).forEach((compasses: Compass[]) => compasses.sort(compassCompare));
    this.manageableCompasses = manageableCompasses;
    this.copies = copies;
  }

  isLoading(): boolean {
    return AppComponent.loading;
  }


  getLoadingArray(): void[] {
    return this.isLoading() ? this.loadingArray : [];
  }

  // manage(compass: Compass) {
  //   const subscription: BulkSubscription = this.subscriptions.find((subscription: BulkSubscription): boolean => subscription.compassSourceId == compass.id);
  //   if(!subscription)
  //     return;
  //   this.dialog.open(CompassLibraryManageDialog, {
  //     width: '880px',
  //     autoFocus: false,
  //     data: {
  //       subscription: subscription,
  //       compasses: [compass].concat(this.copies.get(compass.id) || [])
  //     }
  //   });
  // }

  normalizeName(name: string): string {
    return (name == null ? '' : name).toLowerCase().replace(/\s/g, '');
  }

  matchesFilter(compass: Compass): boolean {
    return !this.filterString
      || this.normalizeName(compass.name || '').includes(this.filterString)
      || this.normalizeName(compass.providerName || '').includes(this.filterString)
      || this.normalizeName(compass.conditionName || '').includes(this.filterString)
  }



  open(compass: Compass) {
    const subscription: BulkSubscription = this.subscriptions.find((subscription: BulkSubscription): boolean => subscription.compassSourceId == compass.id);
    let url : string = '/compass-detail/' + subscription.id;
    console.log(url);
    this.router.navigateByUrl(url);
  }



  getLetters(name: string): string {
    name = name.trim();
    const words: string[] = name.split(/(?: |-)+/g);
    if(words.length == 0)
      return '?';
    if(words.length == 1)
      return words[0].charAt(0).toLocaleUpperCase() + words[0].charAt(1).toLocaleLowerCase();
    return words[0].charAt(0).toLocaleUpperCase() + words[1].charAt(0).toLocaleLowerCase();
  }

  getCompassColumnStyles(): string {
    if(!(this.compassCardsContainer && this.compassCardsContainer.nativeElement))
      return;
    const containerWidth: number = this.compassCardsContainer.nativeElement.offsetWidth;
    const maxColumns: number = Math.floor((containerWidth + this.CARD_GAP) / (this.MIN_CARD_WIDTH + this.CARD_GAP));
    const minColumns: number = Math.ceil((containerWidth + this.CARD_GAP) / (this.APPROX_MAX_CARD_WIDTH + this.CARD_GAP));
    const columns: number = Math.max(2, Math.floor((maxColumns + minColumns) / 2));
    return '--column-count:' + columns.toString() + ';';
  }


  isMultiProvider() : boolean {
    return this.providers && this.providers.length > 1;
  }
}